import { holdAt, midiToFreq, ModSources, velocityGain, type Instrument } from './instrument';
import type { SamplerPreset } from './presets';

// Salamander Grand Piano V3 (Alexander Holm, CC-BY 3.0), one sample every
// minor third. Each note is played from the nearest sample, repitched.
const SAMPLE_NAMES = (() => {
  const names: string[] = [];
  for (let o = 1; o <= 6; o++) for (const n of ['C', 'Ds', 'Fs', 'A']) names.push(`${n}${o}`);
  names.push('C7', 'Ds7');
  return names;
})();

const PC: Record<string, number> = { C: 0, Ds: 3, Fs: 6, A: 9 };

export function sampleNameToMidi(name: string): number {
  const m = /^([A-Z][a-z]?)(\d)$/.exec(name);
  if (!m) throw new Error(`bad sample name ${name}`);
  return (Number(m[2]) + 1) * 12 + PC[m[1]];
}

export function nearestSample(note: number, available: number[]): number {
  let best = available[0];
  for (const s of available) {
    const d = Math.abs(s - note);
    const bd = Math.abs(best - note);
    // On a tie prefer the sample above: pitching down sounds more natural.
    if (d < bd || (d === bd && s > best)) best = s;
  }
  return best;
}

export type PianoBank = Map<number, AudioBuffer>;

let bankPromise: Promise<PianoBank> | null = null;

export function loadPianoBank(ctx: BaseAudioContext, onProgress?: (fraction: number) => void): Promise<PianoBank> {
  if (bankPromise) return bankPromise;
  const bank: PianoBank = new Map();
  let done = 0;
  bankPromise = Promise.all(
    SAMPLE_NAMES.map(async (name) => {
      const res = await fetch(`${import.meta.env.BASE_URL}samples/piano/${name}.mp3`);
      if (!res.ok) throw new Error(`Could not load piano sample ${name}`);
      const buf = await ctx.decodeAudioData(await res.arrayBuffer());
      bank.set(sampleNameToMidi(name), buf);
      onProgress?.(++done / SAMPLE_NAMES.length);
    }),
  ).then(() => bank);
  bankPromise.catch(() => {
    bankPromise = null;
  });
  return bankPromise;
}

interface Voice {
  note: number;
  sources: AudioBufferSourceNode[];
  amp: GainNode;
  held: boolean;
  sustained: boolean;
}

export class Sampler implements Instrument {
  private out: GainNode;
  private tone: AudioNode;
  private voices: Voice[] = [];
  private mods: ModSources;
  private notes: number[];
  private sustain = false;

  constructor(
    private ctx: AudioContext,
    private preset: SamplerPreset,
    private bank: PianoBank,
    destination: AudioNode,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = preset.gain;
    this.out.connect(destination);
    this.tone = this.out;
    if (preset.highShelf) {
      const shelf = ctx.createBiquadFilter();
      shelf.type = 'highshelf';
      shelf.frequency.value = preset.highShelf.freq;
      shelf.gain.value = preset.highShelf.gain;
      shelf.connect(this.out);
      this.tone = shelf;
    }
    this.mods = new ModSources(ctx, { rate: 5, pitch: 0, filter: 0 });
    this.notes = [...bank.keys()].sort((a, b) => a - b);
  }

  noteOn(note: number, velocity: number, t: number): void {
    const n = note + (this.preset.octave ?? 0) * 12;
    this.releaseNote(note, t, 0.06);
    const root = nearestSample(n, this.notes);
    const buffer = this.bank.get(root)!;
    const v = velocity / 127;

    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    // Softer notes on a real piano are darker, not just quieter.
    lp.frequency.value = Math.min(20000, 700 + this.preset.brightness * v * v + midiToFreq(n) * 2);
    lp.Q.value = 0.4;
    const amp = this.ctx.createGain();
    const doubled = !!this.preset.doubleCents;
    const peak = velocityGain(velocity) * (doubled ? 0.7 : 1);
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(peak, t + this.preset.attack);
    lp.connect(amp);
    amp.connect(this.tone);

    const detunes = doubled && this.preset.doubleCents ? [-this.preset.doubleCents, this.preset.doubleCents] : [0];
    const sources = detunes.map((cents) => {
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.detune.value = (n - root) * 100 + cents;
      this.mods.connectPitch(src.detune);
      src.connect(lp);
      src.start(t);
      return src;
    });

    const voice: Voice = { note, sources, amp, held: true, sustained: false };
    sources[0].onended = () => {
      for (const s of sources) this.mods.disconnectPitch(s.detune);
      this.voices = this.voices.filter((x) => x !== voice);
      amp.disconnect();
    };
    this.voices.push(voice);
    if (this.voices.length > 48) this.kill(this.voices[0], t);
  }

  noteOff(note: number, t: number): void {
    for (const v of this.voices) {
      if (v.note !== note || !v.held) continue;
      v.held = false;
      if (this.sustain) v.sustained = true;
      else this.stopVoice(v, t, this.preset.release);
    }
  }

  setSustain(on: boolean, t: number): void {
    this.sustain = on;
    if (on) return;
    for (const v of this.voices) {
      if (v.sustained && !v.held) {
        v.sustained = false;
        this.stopVoice(v, t, this.preset.release);
      }
    }
  }

  allNotesOff(t: number): void {
    this.sustain = false;
    for (const v of this.voices) this.stopVoice(v, t, 0.08);
  }

  setBend(cents: number, t: number): void {
    this.mods.setBend(cents, t);
  }

  setMod(amount: number, t: number): void {
    this.mods.setMod(amount, t);
  }

  dispose(): void {
    const t = this.ctx.currentTime;
    this.allNotesOff(t);
    this.mods.dispose(t + 0.5);
    setTimeout(() => this.out.disconnect(), 600);
  }

  private releaseNote(note: number, t: number, release: number): void {
    for (const v of this.voices) if (v.note === note) this.stopVoice(v, t, release);
  }

  private stopVoice(v: Voice, t: number, release: number): void {
    v.held = false;
    holdAt(v.amp.gain, t);
    v.amp.gain.setTargetAtTime(0, t, Math.max(0.005, release / 4));
    for (const s of v.sources) {
      try {
        s.stop(t + release * 2 + 0.05);
      } catch {
        /* already stopped */
      }
    }
  }

  private kill(v: Voice, t: number): void {
    this.stopVoice(v, t, 0.03);
  }
}
